/* Supply › Reports › Stock › Inventory Snapshot
 * Per lot: opening stock on `from`, every movement during the period, and the
 * closing stock at the end of `to`.
 * Data: GET v1/inventory/snapshot (paged, one store) · CSV via
 * POST v1/reports/stock-snapshot/export (one store or all). */
import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useTaskCenter } from "../../Layout/TaskCenterContext";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import {
  Button,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  KpiBox,
  LoadingState,
  ErrorState,
  InlineAlert,
  DateRangeFilter,
  IconDownload,
} from "../supplyKit";
import { ReportBack, RackCell } from "./reportShared";
import stockSnapshotService from "./stockSnapshotService";
import { PHARMACY_ROUTES } from "../../../Route/path";

const DATE_FMT = "YYYY-MM-DD";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const POLL_INTERVAL = 2500;
const POLL_MAX_ATTEMPTS = 48; // ~2 min before timing out

/* "15 strips + 7 tabs"; strips only when the lot can't be sold loose. */
const fmtStock = (strips, loose, looseOk) => {
  const s = `${cbFmtNum(strips)} strip${strips === 1 ? "" : "s"}`;
  return looseOk && loose ? `${s} + ${cbFmtNum(loose)} tab${loose === 1 ? "" : "s"}` : s;
};

/* A movement cell: blank-looking dash for zero so the real movements stand out. */
const Mv = ({ v, sub, signed }) => (
  <Td mono right style={{ color: v ? undefined : "var(--fg-subtle)" }}>
    {v ? (signed && v > 0 ? `+${cbFmtNum(v)}` : cbFmtNum(v)) : "—"}
    {sub ? <span style={{ color: "var(--fg-muted)", fontSize: 11 }}> {sub}</span> : null}
  </Td>
);

/* Days to expiry counted from the period's end, as the CSV does. */
const ExpiryCell = ({ exp, to }) => {
  if (!exp) return <Td mono>—</Td>;
  const days = moment(exp, DATE_FMT).diff(moment(to, DATE_FMT), "days");
  const color = days < 0 ? "var(--red-700)" : days < 90 ? "var(--amber-700, #b54708)" : "var(--fg-muted)";
  return (
    <Td mono>
      {exp}
      <span style={{ color, fontSize: 11 }}> · {days < 0 ? `expired ${-days}d` : `${days}d`}</span>
    </Td>
  );
};

const StockSnapshot = () => {
  /* The store comes from the topbar, app-wide — see Layout/WarehouseContext. */
  const { code: wh, isAll } = useWarehouse();
  const { runTask } = useTaskCenter();
  const [range, setRange] = useState(() => ({
    from: moment().startOf("month").format(DATE_FMT),
    to: moment().format(DATE_FMT),
  }));
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [pageState, setPageState] = useState({ page: 0, size: 50, totalPages: 0, totalElements: 0 });
  const setPageField = (key, val) =>
    setPageState((s) => ({ ...s, [key]: val, ...(key === "size" ? { page: 0 } : {}) }));

  /* The range picker clears `to` while a new range is being picked, so only a
     complete range is queried. */
  const rangeReady = !!(range.from && range.to);

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [searchTerm, wh, range.from, range.to]);

  useEffect(() => {
    if (!wh || !rangeReady) {
      setRows([]);
      setLoading(false);
      /* The list is per store; "All stores" is only available as a CSV. */
      setError(
        !wh && isAll
          ? "The on-screen snapshot covers one store. Pick a store in the topbar, or use Export CSV for all stores."
          : null
      );
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    stockSnapshotService
      .list({
        warehouseId: wh,
        from: range.from,
        to: range.to,
        search: searchTerm,
        page: pageState.page,
        size: pageState.size,
      })
      .then((res) => {
        if (!alive) return;
        setRows(res.content);
        setPageState((s) => ({ ...s, totalPages: res.totalPages, totalElements: res.totalElements }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load the inventory snapshot.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, isAll, rangeReady, range.from, range.to, searchTerm, pageState.page, pageState.size, reloadKey]);

  const startExport = () => {
    if (exporting) return;
    if (!rangeReady) {
      toast.info("Pick a start and end date before exporting.");
      return;
    }
    if (!wh && !isAll) {
      toast.info("Pick a store before exporting.");
      return;
    }
    setExporting(true);
    const req = { warehouseId: isAll ? "" : wh, from: range.from, to: range.to, search: searchTerm };
    runTask({
      title: `Inventory Snapshot · ${isAll ? "All stores" : wh} · ${range.from} → ${range.to}`,
      run: async (onProgress) => {
        onProgress("Requesting…");
        const task = await stockSnapshotService.requestExport(req);
        if (!task?.taskId) throw new Error("Export did not return a task id.");
        const fileName =
          task.fileName || `stock-snapshot-${isAll ? "all-stores" : wh}-${range.from}-to-${range.to}.csv`;
        const doDownload = () => stockSnapshotService.download(task.taskId, fileName);

        let cur = task;
        for (let i = 0; cur.status === "PENDING" && i < POLL_MAX_ATTEMPTS; i++) {
          onProgress("Generating report…");
          await sleep(POLL_INTERVAL);
          cur = await stockSnapshotService.exportStatus(task.taskId);
        }
        if (cur.status === "FAILED") throw new Error(cur.message || "Report generation failed.");
        if (cur.status !== "READY") throw new Error("Report generation timed out. Please try again.");

        onProgress("Downloading…");
        await doDownload();
        return {
          detail: `Downloaded${cur.rowCount != null ? ` · ${cbFmtNum(cur.rowCount)} rows` : ""}`,
          action: { label: "Download again", run: doDownload },
        };
      },
    });
    toast.info("Generating report — track progress in notifications.");
    setTimeout(() => setExporting(false), 1200);
  };

  /* Page-level figures only — the list is paged and has no totals endpoint. */
  const pageClosingValue = rows.reduce((s, r) => s + r.closingStrips * r.purchase, 0);
  const pageSold = rows.reduce((s, r) => s + r.salesOut, 0);
  const pageReceived = rows.reduce((s, r) => s + r.purchaseIn, 0);

  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.STOCK_REPORT} label="Stock Reports" />
      <SectionHeader
        title="Inventory Snapshot"
        sub="Opening stock, movements and closing stock per batch for a period"
        actions={
          <Button
            icon={<IconDownload size={14} />}
            onClick={startExport}
            disabled={exporting || !rangeReady || (!wh && !isAll)}
          >
            Export CSV{isAll ? " · All stores" : ""}
          </Button>
        }
      />

      <ToolbarBar>
        <DateRangeFilter
          label="Period"
          from={range.from}
          to={range.to}
          onFromChange={(v) => setRange((r) => ({ ...r, from: v }))}
          onToChange={(v) => setRange((r) => ({ ...r, to: v }))}
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search medicine, code, batch or category…"
          style={{ width: 300, maxWidth: "100%" }}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} batches
        </div>
      </ToolbarBar>

      {!rangeReady && <InlineAlert tone="amber">Pick an end date to complete the period.</InlineAlert>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Batches" value={cbFmtNum(pageState.totalElements)} variant="ink" />
        <KpiBox label="Strips received (this page)" value={cbFmtNum(pageReceived)} variant="ink" />
        <KpiBox label="Strips sold (this page)" value={cbFmtNum(pageSold)} variant="ink" />
        <KpiBox label="Closing stock cost (this page)" value={cbFmtINR(pageClosingValue)} variant="green" />
      </div>

      {loading ? (
        <LoadingState label="Loading inventory snapshot…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            sticky
            columns={[
              { label: "Medicine" },
              { label: "Batch" },
              { label: "Expiry" },
              { label: "Rack" },
              { label: "Opening", align: "right" },
              { label: "Purchase in", align: "right" },
              { label: "Sales return", align: "right" },
              { label: "Transfer in", align: "right" },
              { label: "Sales out", align: "right" },
              { label: "Purch. return", align: "right" },
              { label: "Transfer out", align: "right" },
              { label: "Expired / dmg", align: "right" },
              { label: "Adjustment", align: "right" },
              { label: "Closing", align: "right" },
              { label: "Stock value", align: "right" },
            ]}
          >
            {rows.length === 0 && (
              <tr>
                <Td colSpan={15} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                  No batches found for this period.
                </Td>
              </tr>
            )}
            {rows.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <div style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{r.medicineName}</div>
                  <div style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                    {r.medId}
                    {r.unitPack ? ` · ${r.unitPack}` : ""}
                  </div>
                </Td>
                <Td mono>{r.batch}</Td>
                <ExpiryCell exp={r.exp} to={range.to} />
                <RackCell lot={r} />
                <Td mono right>
                  {fmtStock(r.openingStrips, r.openingLoose, r.looseSaleSupported)}
                </Td>
                <Mv v={r.purchaseIn} />
                <Mv
                  v={r.salesReturnIn}
                  sub={r.looseSaleSupported && r.looseReturned ? `+ ${cbFmtNum(r.looseReturned)} tabs` : null}
                />
                <Mv v={r.transferIn} />
                <Mv
                  v={r.salesOut}
                  sub={r.looseSaleSupported && r.looseSold ? `+ ${cbFmtNum(r.looseSold)} tabs` : null}
                />
                <Mv v={r.purchaseReturnOut} />
                <Mv v={r.transferOut} />
                <Mv v={r.expiredDamaged} />
                <Mv v={r.adjustment} signed />
                <Td mono right style={{ fontWeight: 600 }}>
                  {fmtStock(r.closingStrips, r.closingLoose, r.looseSaleSupported)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }} title="Closing strips × today's purchase price">
                  {cbFmtINR(r.closingStrips * r.purchase)}
                </Td>
              </Tr>
            ))}
          </Table>

          <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
            Movements are in strips; loose tablets sold or returned are shown next to them. Strip counts may not add up
            exactly where loose sales opened a strip. Prices, stock value and rack are today&apos;s values.
          </div>

          <ToolbarBar>
            <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {start}–{end} of {cbFmtNum(pageState.totalElements)}
              </span>
              <Button
                size="sm"
                disabled={pageState.page <= 0}
                onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={pageState.page + 1 >= pageState.totalPages}
                onClick={() => setPageField("page", pageState.page + 1)}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}
    </div>
  );
};

export default StockSnapshot;
